import React, { useState, useEffect } from 'react';
import '@/App.css';
import { Search, Plus, FileText, Archive, Lightbulb, Menu } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getDailyPrompt } from '@/constants/dashboard';
import Sidebar from '@/components/dashboard/Sidebar';
import StatsDashboard from '@/components/dashboard/StatsDashboard';
import DailyPrompt from '@/components/dashboard/DailyPrompt';
import IdeaCard from '@/components/dashboard/IdeaCard';
import IdeaDialog from '@/components/dashboard/IdeaDialog';
import TemplateDialog from '@/components/dashboard/TemplateDialog';
import BusinessAnalytics from '@/components/dashboard/BusinessAnalytics';
import { DownloadGuideDialog, HelpDialog } from '@/components/dashboard/InfoDialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const [ideas, setIdeas] = useState([]);
  const [filteredIdeas, setFilteredIdeas] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [enhancingId, setEnhancingId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDownloadGuide, setShowDownloadGuide] = useState(false);
  const [showHelpTutorial, setShowHelpTutorial] = useState(false);
  const [viewMode, setViewMode] = useState('board'); // default to board
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'idea', 'in_progress', 'done'

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('Work');
  const [formTags, setFormTags] = useState([]);
  const [formTagInput, setFormTagInput] = useState('');
  const [formStatus, setFormStatus] = useState('idea');

  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: fetch data on initial load
  useEffect(() => {
    fetchIdeas();
    const dismissedDate = localStorage.getItem('promptDismissedDate');
    if (dismissedDate === new Date().toDateString()) setShowPrompt(false);
  }, []);

  // Keyboard shortcuts
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: register global shortcuts once
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.closest('.ql-editor')) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') { e.preventDefault(); openDialog(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); document.querySelector('[data-testid="search-input"]')?.focus(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchIdeas = async () => {
    try {
      const { data } = await axios.get(`${API}/ideas`, { withCredentials: true });
      // Ensure all ideas have a status field
      const normalized = data.map(i => ({ ...i, status: i.status || 'idea' }));
      setIdeas(normalized);
    } catch (error) {
      console.error('Error fetching ideas:', error);
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = ideas.filter(idea => idea.archived === showArchived);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(idea =>
        idea.title.toLowerCase().includes(q) ||
        idea.content.replace(/<[^>]*>/g, '').toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(idea => idea.category === selectedCategory);
    }
    if (selectedTags.length > 0) {
      filtered = filtered.filter(idea => selectedTags.every(tag => idea.tags.includes(tag)));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(idea => (idea.status || 'idea') === statusFilter);
    }
    setFilteredIdeas(filtered);
  }, [ideas, searchQuery, selectedCategory, selectedTags, showArchived, statusFilter]);

  const getAllTags = () => {
    const tags = new Set();
    ideas.forEach(idea => idea.tags.forEach(tag => tags.add(tag)));
    return Array.from(tags);
  };

  const openDialog = (idea = null) => {
    if (idea) {
      setEditingIdea(idea);
      setFormTitle(idea.title);
      setFormContent(idea.content);
      setFormCategory(idea.category);
      setFormTags(idea.tags);
      setFormStatus(idea.status || 'idea');
    } else {
      setEditingIdea(null);
      setFormTitle('');
      setFormContent('');
      setFormCategory('Work');
      setFormTags([]);
      setFormStatus('idea');
    }
    setFormTagInput('');
    setIsDialogOpen(true);
    setSidebarOpen(false);
  };

  const openTemplateDialog = () => {
    setIsTemplateDialogOpen(true);
    setSidebarOpen(false);
  };

  const selectTemplate = (template) => {
    setFormTitle('');
    setFormContent(template.content);
    setFormCategory(template.category);
    setFormTags([]);
    setFormTagInput('');
    setEditingIdea(null);
    setIsTemplateDialogOpen(false);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingIdea(null);
  };

  const saveIdea = async () => {
    if (!formTitle.trim()) { toast.error('Please enter a title'); return; }
    const cleanContent = formContent?.replace(/<p><br><\/p>/g, '').trim() || '';
    try {
      const ideaData = { title: formTitle, content: cleanContent, category: formCategory, tags: formTags };
      if (editingIdea) {
        await axios.put(`${API}/ideas/${editingIdea._id}`, ideaData, { withCredentials: true });
        // Update status separately via dedicated endpoint
        if (formStatus !== (editingIdea.status || 'idea')) {
          await axios.put(`${API}/business-analytics/ideas/${editingIdea._id}/status`, { status: formStatus });
        }
        toast.success('Idea updated successfully');
      } else {
        await axios.post(`${API}/ideas`, ideaData, { withCredentials: true });
        toast.success('Idea created successfully');
      }
      await fetchIdeas();
      closeDialog();
    } catch (error) {
      console.error('Error saving idea:', error);
      toast.error('Failed to save idea');
    }
  };

  const changeStatus = async (ideaId, newStatus) => {
    try {
      await axios.put(`${API}/business-analytics/ideas/${ideaId}/status`, { status: newStatus });
      setIdeas(prev => prev.map(i => i._id === ideaId ? { ...i, status: newStatus } : i));
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteIdea = async (id) => {
    try {
      await axios.delete(`${API}/ideas/${id}`, { withCredentials: true });
      toast.success('Idea deleted');
      await fetchIdeas();
    } catch (error) {
      toast.error('Failed to delete idea');
    }
  };

  const toggleArchive = async (id) => {
    try {
      const idea = ideas.find(i => i._id === id);
      await axios.put(`${API}/ideas/${id}`, { archived: !idea.archived }, { withCredentials: true });
      toast.success(idea.archived ? 'Idea restored' : 'Idea archived');
      await fetchIdeas();
    } catch (error) {
      toast.error('Failed to archive idea');
    }
  };

  const enhanceIdea = async (idea) => {
    // Guard: don't enhance empty content
    const textContent = idea.content?.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      toast.error('Add some content to the idea before enhancing');
      return;
    }
    setEnhancingId(idea._id);
    try {
      const response = await axios.post(`${API}/enhance-idea`, { title: idea.title, content: idea.content }, { withCredentials: true, timeout: 35000 });
      await axios.put(`${API}/ideas/${idea._id}`, { content: response.data.enhanced_content }, { withCredentials: true });
      toast.success('Idea enhanced with AI!');
      await fetchIdeas();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 504 || error.code === 'ECONNABORTED') {
        toast.error('AI took too long. Try with shorter content or try again.');
      } else if (error.response?.status === 503) {
        toast.error('AI service temporarily unavailable. Try again in a moment.');
      } else if (error.response?.status === 402) {
        toast.error('AI quota exceeded. Admin needs to add balance.');
      } else {
        toast.error(detail || 'Failed to enhance idea. Please try again.');
      }
    } finally {
      setEnhancingId(null);
    }
  };

  const toggleTagFilter = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const dismissPrompt = () => {
    setShowPrompt(false);
    localStorage.setItem('promptDismissedDate', new Date().toDateString());
  };

  const handleExportIdeas = async () => {
    try {
      const res = await axios.get(`${API}/ideas/export/markdown`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'vault-ideas-export.md'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Ideas exported!');
    } catch (err) { toast.error('Export failed'); }
  };

  const dailyPrompt = getDailyPrompt();

  return (
    <div className="flex h-screen bg-[var(--main-bg)] overflow-hidden relative">
      <Toaster position="top-right" />
      
      {/* Mobile Menu Button */}
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 selected-state p-2.5 border border-[var(--border-default)] rounded-sm"
        style={{ width: '40px', height: '40px' }}
      >
        <Menu size={18} />
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          data-testid="mobile-overlay"
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30 cursor-pointer"
          onClick={() => setSidebarOpen(false)}
          style={{ pointerEvents: 'auto' }}
        />
      )}
      
      <Sidebar
        user={user}
        dark={dark}
        toggleTheme={toggleTheme}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedTags={selectedTags}
        toggleTagFilter={toggleTagFilter}
        allTags={getAllTags()}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        setShowStats={setShowStats}
        setShowDownloadGuide={setShowDownloadGuide}
        setShowHelpTutorial={setShowHelpTutorial}
        logout={logout}
        onExportIdeas={handleExportIdeas}
      />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto w-full">
        <div className="max-w-6xl mx-auto p-4 pt-14 sm:p-8 lg:p-12 w-full lg:pt-12">
          {showStats ? (
            <StatsDashboard ideas={ideas} onClose={() => setShowStats(false)} />
          ) : (
            <>
              {/* Daily Prompt */}
              {showPrompt && !showArchived && (
                <DailyPrompt
                  prompt={dailyPrompt}
                  onDismiss={dismissPrompt}
                  onCapture={() => openDialog()}
                />
              )}

              {/* Business Analytics - Business tier only */}
              {(user?.subscription_tier === 'business' || user?.subscription_tier === 'founding' || user?.role === 'admin') && (
                <BusinessAnalytics key={ideas.length} />
              )}

              {/* Header */}
              <div className="mb-8 lg:mb-12">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0 mb-6 lg:mb-8">
                  <div>
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {showArchived ? 'Archived Ideas' : 'Active Ideas'}
                    </h2>
                    <p className="text-sm sm:text-base text-[var(--text-secondary)]">
                      {filteredIdeas.length} {filteredIdeas.length === 1 ? 'idea' : 'ideas'}
                    </p>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      data-testid="template-btn"
                      onClick={openTemplateDialog}
                      className="flex-1 sm:flex-initial bg-[var(--surface)] text-[var(--text-primary)] font-bold px-4 sm:px-6 py-3 border border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors duration-150 flex items-center justify-center gap-2"
                    >
                      <FileText size={20} />
                      <span className="hidden sm:inline">Templates</span>
                    </button>
                    <button
                      data-testid="new-idea-btn"
                      onClick={() => openDialog()}
                      className="flex-1 sm:flex-initial bg-[var(--primary)] text-white font-bold px-4 sm:px-6 py-3 border border-[var(--border-default)] brutalist-shadow brutalist-shadow-hover brutalist-shadow-active flex items-center justify-center gap-2 transition-all duration-200"
                    >
                      <Plus size={20} />
                      New Idea
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" size={20} />
                  <input
                    data-testid="search-input"
                    type="text"
                    placeholder="Search ideas..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-[var(--surface)] border border-[var(--border-default)] pl-12 pr-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-all"
                  />
                </div>
              </div>

              {/* Status Filter + View Toggle */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'idea', label: 'Idea' },
                    { id: 'in_progress', label: 'In Progress' },
                    { id: 'done', label: 'Done' },
                  ].map(s => (
                    <button
                      key={s.id}
                      data-testid={`status-filter-${s.id}`}
                      onClick={() => setStatusFilter(s.id)}
                      className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
                        statusFilter === s.id
                          ? 'bg-[var(--text-primary)] text-[var(--main-bg)] border-[var(--text-primary)]'
                          : 'border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {s.label}
                      {s.id !== 'all' && (
                        <span className="ml-1 opacity-60">
                          {ideas.filter(i => !i.archived && (i.status || 'idea') === s.id).length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1 border border-[var(--border-default)]">
                  <button
                    data-testid="view-list"
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1.5 text-xs font-bold ${viewMode === 'list' ? 'bg-[var(--text-primary)] text-[var(--main-bg)]' : 'text-[var(--text-muted)]'}`}
                  >
                    List
                  </button>
                  <button
                    data-testid="view-board"
                    onClick={() => setViewMode('board')}
                    className={`px-3 py-1.5 text-xs font-bold ${viewMode === 'board' ? 'bg-[var(--text-primary)] text-[var(--main-bg)]' : 'text-[var(--text-muted)]'}`}
                  >
                    Board
                  </button>
                </div>
              </div>

              {/* Ideas Display */}
              {filteredIdeas.length === 0 ? (
                <div className="text-center py-12 sm:py-20">
                  <div className="mb-8 relative inline-block">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-[var(--surface)] border-2 border-[var(--border-default)] flex items-center justify-center relative overflow-hidden group">
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[#FFD60A]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="relative z-10">
                        {showArchived ? <Archive size={40} className="text-[var(--text-secondary)]" /> : <Lightbulb size={40} className="text-[var(--text-secondary)]" />}
                      </div>
                    </div>
                    <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-[#FF3B30]"></div>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-[#007AFF]"></div>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    No ideas {showArchived ? 'archived' : 'yet'}
                  </h3>
                  <p className="text-sm sm:text-base text-[var(--text-secondary)] mb-8 max-w-md mx-auto">
                    {showArchived ? 'Your archived ideas will appear here' : 'Your next brilliant idea is just a click away. Start capturing your thoughts!'}
                  </p>
                  {!showArchived && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        data-testid="empty-state-template-btn"
                        onClick={openTemplateDialog}
                        className="bg-[var(--surface)] text-[var(--text-primary)] font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-all duration-150 btn-shine"
                      >
                        Use Template
                      </button>
                      <button
                        data-testid="empty-state-new-idea-btn"
                        onClick={() => openDialog()}
                        className="selected-state font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform duration-150 btn-shine"
                      >
                        Create First Idea
                      </button>
                    </div>
                  )}
                </div>
              ) : viewMode === 'board' ? (
                /* Board View - 3 columns */
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['idea', 'in_progress', 'done'].map(col => {
                    const colIdeas = filteredIdeas.filter(i => (i.status || 'idea') === col);
                    const colConfig = {
                      idea: { label: 'Ideas', color: 'bg-[var(--text-muted)]' },
                      in_progress: { label: 'In Progress', color: 'bg-[var(--secondary)]' },
                      done: { label: 'Done', color: 'bg-[#30D158]' },
                    };
                    const cfg = colConfig[col];
                    return (
                      <div key={col} className="border-2 border-[var(--border-default)] bg-[var(--surface-alt)] min-h-[200px]">
                        <div className="flex items-center gap-2 p-3 border-b-2 border-[var(--border-default)]">
                          <div className={`w-3 h-3 ${cfg.color}`} />
                          <span className="text-xs font-bold uppercase tracking-widest">{cfg.label}</span>
                          <span className="text-xs text-[var(--text-muted)] ml-auto">{colIdeas.length}</span>
                        </div>
                        <div className="p-2 space-y-2">
                          {colIdeas.map(idea => (
                            <IdeaCard
                              key={idea._id}
                              idea={idea}
                              enhancingId={enhancingId}
                              onEnhance={enhanceIdea}
                              onEdit={openDialog}
                              onToggleArchive={toggleArchive}
                              onDelete={deleteIdea}
                              onStatusChange={changeStatus}
                            />
                          ))}
                          {colIdeas.length === 0 && (
                            <p className="text-xs text-[var(--text-muted)] text-center py-8">No ideas</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {filteredIdeas.map(idea => (
                    <IdeaCard
                      key={idea._id}
                      idea={idea}
                      enhancingId={enhancingId}
                      onEnhance={enhanceIdea}
                      onEdit={openDialog}
                      onToggleArchive={toggleArchive}
                      onDelete={deleteIdea}
                      onStatusChange={changeStatus}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <TemplateDialog
        open={isTemplateDialogOpen}
        onOpenChange={setIsTemplateDialogOpen}
        onSelect={selectTemplate}
      />

      <IdeaDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingIdea={editingIdea}
        formTitle={formTitle}
        setFormTitle={setFormTitle}
        formContent={formContent}
        setFormContent={setFormContent}
        formCategory={formCategory}
        setFormCategory={setFormCategory}
        formTags={formTags}
        setFormTags={setFormTags}
        formTagInput={formTagInput}
        setFormTagInput={setFormTagInput}
        formStatus={formStatus}
        setFormStatus={setFormStatus}
        onSave={saveIdea}
        onClose={closeDialog}
      />

      <DownloadGuideDialog open={showDownloadGuide} onOpenChange={setShowDownloadGuide} />
      <HelpDialog open={showHelpTutorial} onOpenChange={setShowHelpTutorial} />
    </div>
  );
};

export default Dashboard;
